import { NativeModules, Platform } from 'react-native';
import {
  API_HOST,
  ApiError,
  awaitAuthHydration,
  fetchBinaryWithAuth,
  fetchWithAuth,
  getAuthStore,
} from './client';

async function fetchTaskControl(path, options, authorizeRequest) {
  const send = async (guard = {}) => {
    const sign = guard.authorizeRequest;
    const current = () => (!options.isRequestCurrent || options.isRequestCurrent()) && (!guard.isRequestCurrent || guard.isRequestCurrent());
    // Admission conflicts mean this turn was never dispatched. Concurrent
    // approval cards can hit that short account-wide lease; retry only this
    // explicit rejection, never an ambiguous network failure or another 409.
    const fixedControllerProof = !sign && (options.headers?.['x-gab-remote-controller'] || guard.headers?.['x-gab-remote-controller']);
    const admissionBackoff = !fixedControllerProof && /\/(?:message|resume)$/.test(path) ? [250, 750, 1500, 2500] : [];
    const body = options.body == null ? options.body : JSON.parse(JSON.stringify(options.body));
    for (let attempt = 0; ; attempt += 1) {
      if (!current()) throw new Error('Your account changed. Open Work again.');
      // Sign the same JSON value on every attempt, with a fresh native proof.
      // Never replay a consumed native signature after an admission rejection.
      const proofHeaders = sign ? await sign(path, { method: options.method, body: body || {} }) : null;
      if (!current()) throw new Error('Your account changed. Open Work again.');
      const headers = options.headers || guard.headers || proofHeaders ? { ...options.headers, ...guard.headers, ...proofHeaders } : undefined;
      try {
        return await fetchWithAuth(path, { ...options, ...(body !== undefined && { body }), ...(headers && { headers }),
          ...((options.isRequestCurrent || guard.isRequestCurrent) && { isRequestCurrent: current }),
          ...((sign || headers?.['x-gab-remote-controller']) && { networkRetries: 0 }) });
      } catch (error) {
        if (error?.status !== 409 || error?.data?.reason !== 'work_session_starting'
            || attempt >= admissionBackoff.length) throw error;
        await new Promise(resolve => setTimeout(resolve, admissionBackoff[attempt]));
      }
    }
  };
  if (authorizeRequest || options.headers?.['x-gab-remote-controller'] || options.isRequestCurrent) return send({ authorizeRequest });
  const taskId = /^\/json\/tasks\/([a-f0-9]{24})\//.exec(path)?.[1];
  if (!taskId) return send();
  // Approvals/settings may be called outside the message store. Resolve the
  // current task on its platform instead of relying on stale chat-list data.
  return require('../remoteWork').withRemoteWorkTask({ id: taskId }, send);
}

// Work threads: control-plane CRUD, live events, steering, artifacts,
// checkpoints, browser handoff, and schedules.
export const coworkerApi = {
  prepareTaskBoundary: async (taskId, body) => fetchTaskControl(`/json/tasks/${encodeURIComponent(taskId)}/boundary`, { method: 'POST', body }),
  fetchTaskHandoffs: async (taskId) => fetchWithAuth(`/json/tasks/${encodeURIComponent(taskId)}/handoffs`),
  cancelTaskHandoff: async (taskId, requestId) => fetchTaskControl(`/json/tasks/${encodeURIComponent(taskId)}/handoffs/${encodeURIComponent(requestId)}/cancel`, { method: 'POST' }),
  fetchVideoTimeline: async (taskId) => {
    const data = await fetchWithAuth(`/json/tasks/${encodeURIComponent(taskId)}/video-timeline`);
    if (data.error) throw new ApiError(data.message || 'Could not load timeline', 400, data);
    return data.timeline;
  },

  saveVideoTimeline: async (taskId, clips) => {
    const data = await fetchTaskControl(`/json/tasks/${encodeURIComponent(taskId)}/video-timeline`, {
      method: 'POST', body: { clips: clips.map(({ id, index, path, excluded }) => ({ id, index, ...(path ? { path } : {}), excluded: !!excluded })) },
    });
    if (data.error) throw new ApiError(data.message || 'Could not save timeline', 400, data);
    return data.timeline;
  },

  fetchTasks: async () => {
    const data = await fetchWithAuth('/json/tasks');
    if (data.error) throw new ApiError(data.message || 'Failed to fetch tasks', 400, data);
    return data.tasks;
  },

  fetchStarterPrompts: async ({ collectionId, project } = {}) => {
    const qs = collectionId ? `?collectionId=${encodeURIComponent(collectionId)}` : '';
    const data = project
      ? await fetchWithAuth('/json/work/project-starter-prompts', { method: 'POST', body: { project }, timeoutMs: 45000 })
      : await fetchWithAuth(`/json/work/starter-prompts${qs}`, { timeoutMs: 45000 });
    if (data.error) throw new ApiError(data.message || 'Failed to load starter prompts', data.status || 400, data);
    return data;
  },

  fetchTask: async (taskId) => {
    const data = await fetchWithAuth(`/json/tasks/${encodeURIComponent(taskId)}`);
    // A failed task is still a successful detail response: its `error` is
    // the saved failure text. Only the boolean API envelope rejects here.
    if (data.error === true) throw new ApiError(data.message || 'Failed to fetch task', data.status || 400, data);
    return data;
  },

  createTask: async ({ title, prompt, model, thinkingPower, orchestratorModel, reasoningEffort, budgetTokens, includeMemory, autoApprove, actionApproval, collectionId, fileIds, agentId, localProject, localDesktop }, { isRequestCurrent } = {}) => {
    const options = {
      ...(isRequestCurrent && { isRequestCurrent }),
      method: 'POST',
      timeoutMs: 90000,
      body: {
        title,
        prompt,
        ...(model && { model }),
        ...(thinkingPower && { thinkingPower }),
        ...(orchestratorModel && { orchestratorModel }),
        ...(reasoningEffort && { reasoningEffort }),
        ...(budgetTokens && { budgetTokens }),
        ...(includeMemory !== undefined && { includeMemory }),
        ...(autoApprove !== undefined && { autoApprove }),
        ...(actionApproval !== undefined && { actionApproval }),
        ...(collectionId && { collectionId }),
        ...(Array.isArray(fileIds) && fileIds.length && { fileIds }),
        ...(agentId && { agentId }),
        ...(localProject && { localProject }),
        ...(localDesktop && { localDesktop }),
        ...(Platform.OS === 'macos' && { browserMode: 'mac' }),
      },
    };
    if (localProject || localDesktop) {
      if (Platform.OS !== 'macos') throw new Error('Update Gab AI and start this task from its Mac.');
      if (isRequestCurrent && !isRequestCurrent()) throw new Error('Your account changed. Open Work again.');
      options.body = JSON.parse(JSON.stringify(options.body));
      options.headers = {};
      for (const [enabled, module, method, header] of [
        [localProject, 'MacProjectFolder', 'authorizeOriginalLocalLaunch', 'x-gab-original-local-proof'],
        [localDesktop, 'MacDesktopAccess', 'authorizeOriginalDesktopLaunch', 'x-gab-original-desktop-proof'],
      ]) if (enabled) {
        if (!NativeModules[module]?.[method]) throw new Error('Update Gab AI and start this task from its Mac.');
        const proof = await NativeModules[module][method](String(getAuthStore().getState().user?.id), JSON.stringify(options.body));
        if (isRequestCurrent && !isRequestCurrent()) throw new Error('Your account changed. Open Work again.');
        options.headers[header] = JSON.stringify(proof);
      }
      options.networkRetries = 0;
    }
    const data = await fetchWithAuth('/json/tasks', options).catch((error) => {
      if (error?.status === 404) {
        throw new ApiError('Work is unavailable on the server. Please try again later.', 404);
      }
      throw error;
    });
    if (data.error) throw new ApiError(data.message || 'Failed to create task', data.status || 400, data);
    return data;
  },

  controlGoal: async (taskId, action, goal, expectedTurnId) => {
    const data = await fetchTaskControl(`/json/tasks/${encodeURIComponent(taskId)}/goal`, {
      method: 'POST', body: { action, expectedGoalId: goal?.id || null,
        expectedRevision: goal?.revision || 0, expectedTurnId: expectedTurnId || null },
    });
    if (data.error) throw new ApiError(data.message || 'Could not update the goal', data.status || 400, data);
    return data;
  },

  setTaskActionApproval: async (taskId, actionApproval) => {
    const data = await fetchTaskControl(`/json/tasks/${encodeURIComponent(taskId)}/action-approval`, {
      method: 'POST', body: { actionApproval },
    });
    if (data.error) throw new ApiError(data.message || 'Could not change action approvals', data.status || 400, data);
    return data;
  },

  setTaskAutoApprove: async (taskId, autoApprove) => {
    const data = await fetchTaskControl(`/json/tasks/${encodeURIComponent(taskId)}/auto-approve`, {
      method: 'POST', body: { autoApprove },
    });
    if (data.error) throw new ApiError(data.message || 'Failed to change Auto-approve', data.status || 400, data);
    return data;
  },

  getOrchestratorModels: async () => {
    const data = await fetchWithAuth('/json/inference-tasks/models?executionMode=work');
    if (data.error) throw new ApiError(data.message || 'Could not load models', data.status || 400, data);
    return data.models || data.data?.models || [];
  },

  setTaskOrchestrator: async (taskId, settings) => {
    const data = await fetchTaskControl(`/json/tasks/${encodeURIComponent(taskId)}/orchestrator`, {
      method: 'POST', body: settings,
    });
    if (data.error) throw new ApiError(data.message || 'Could not save main model', data.status || 400, data);
    return data;
  },

  setTaskThinkingPower: async (taskId, thinkingPower) => {
    const data = await fetchTaskControl(`/json/tasks/${encodeURIComponent(taskId)}/model`, {
      method: 'POST',
      body: { thinkingPower },
    });
    if (data.error) throw new ApiError(data.message || 'Failed to change thinking power', data.status || 400, data);
    return data;
  },

  steerTask: async (taskId, message, fileIds, routingOptions = {}, { isRequestCurrent, headers, authorizeRequest } = {}) => {
    const data = await fetchTaskControl(`/json/tasks/${encodeURIComponent(taskId)}/steer`, {
      method: 'POST',
      ...(headers && { headers }),
      body: { ...routingOptions, message, ...(Array.isArray(fileIds) && fileIds.length && { fileIds }) },
      ...(isRequestCurrent && { isRequestCurrent }),
    }, authorizeRequest);
    if (data.error) throw new ApiError(data.message || 'Failed to steer task', data.status || 400, data);
    return data;
  },

  stopTask: async (taskId, expectedTurnId) => {
    const data = await fetchWithAuth(`/json/tasks/${encodeURIComponent(taskId)}/stop`, {
      method: 'POST', body: { expectedTurnId: expectedTurnId || null },
    });
    if (data.error) throw new ApiError(data.message || 'Failed to stop task', data.status || 400, data);
    return data;
  },

  resumeTask: async (taskId, message, fileIds, routingOptions = {}, { isRequestCurrent, headers, authorizeRequest } = {}) => {
    const data = await fetchTaskControl(`/json/tasks/${encodeURIComponent(taskId)}/resume`, {
      method: 'POST',
      ...(headers && { headers }),
      body: { ...routingOptions, message, ...(Array.isArray(fileIds) && fileIds.length && { fileIds }) },
      ...(isRequestCurrent && { isRequestCurrent }),
    }, authorizeRequest);
    if (data.error) throw new ApiError(data.message || 'Failed to resume task', data.status || 400, data);
    return data;
  },

  sendThreadMessage: async (threadId, message, fileIds, routingOptions = {}, { isRequestCurrent, headers, authorizeRequest } = {}) => {
    const data = await fetchTaskControl(`/json/tasks/${encodeURIComponent(threadId)}/message`, {
      method: 'POST',
      ...(headers && { headers }),
      body: { ...routingOptions, message, ...(Array.isArray(fileIds) && fileIds.length && { fileIds }) },
      ...(isRequestCurrent && { isRequestCurrent }),
    }, authorizeRequest);
    if (data.error) throw new ApiError(data.message || 'Failed to send message', data.status || 400, data);
    return data;
  },

  enqueueFollowUp: async (taskId, item, { headers, isRequestCurrent, authorizeRequest } = {}) => {
    const data = await fetchTaskControl(`/json/tasks/${encodeURIComponent(taskId)}/follow-ups`, { method: 'POST', body: item, headers, isRequestCurrent }, authorizeRequest);
    if (data.error) throw new ApiError(data.message || 'Failed to queue follow-up', data.status || 400, data);
    return data.followUpQueue || [];
  },

  updateFollowUp: async (taskId, id, patch, { headers, isRequestCurrent, authorizeRequest } = {}) => {
    const data = await fetchTaskControl(`/json/tasks/${encodeURIComponent(taskId)}/follow-ups/${encodeURIComponent(id)}`, { method: 'PATCH', body: patch, headers, isRequestCurrent }, authorizeRequest);
    if (data.error) throw new ApiError(data.message || 'Failed to update follow-up', data.status || 400, data);
    return data.followUpQueue || [];
  },

  removeFollowUp: async (taskId, id) => {
    const data = await fetchTaskControl(`/json/tasks/${encodeURIComponent(taskId)}/follow-ups/${encodeURIComponent(id)}`, { method: 'DELETE' });
    if (data.error) throw new ApiError(data.message || 'Failed to remove follow-up', data.status || 400, data);
    return data.followUpQueue || [];
  },

  reorderFollowUps: async (taskId, ids) => {
    const data = await fetchTaskControl(`/json/tasks/${encodeURIComponent(taskId)}/follow-ups/reorder`, { method: 'POST', body: { ids } });
    if (data.error) throw new ApiError(data.message || 'Failed to reorder follow-ups', data.status || 400, data);
    return data.followUpQueue || [];
  },

  clearFollowUps: async (taskId) => {
    const data = await fetchTaskControl(`/json/tasks/${encodeURIComponent(taskId)}/follow-ups`, { method: 'DELETE' });
    if (data.error) throw new ApiError(data.message || 'Failed to clear follow-ups', data.status || 400, data);
    return data.followUpQueue || [];
  },

  fetchTaskEvents: async (taskId, since = 0, { kinds, tail = 500 } = {}) => {
    const params = new URLSearchParams({ since: String(since || 0) });
    if (kinds) params.set('kinds', String(kinds));
    else if (!Number(since)) params.set('tail', String(Math.max(1, Math.min(500, Number(tail) || 500))));
    const data = await fetchWithAuth(
      `/json/tasks/${encodeURIComponent(taskId)}/events.json?${params.toString()}`
    );
    if (data.error) throw new ApiError(data.message || 'Failed to fetch events', data.status || 400, data);
    return data;
  },

  getArtifactDownloadUrl: async (taskId, artifactPath, { isRequestCurrent } = {}) => {
    const encodedPath = artifactPath.split('/').map(encodeURIComponent).join('/');
    const data = await fetchWithAuth(
      `/json/tasks/${encodeURIComponent(taskId)}/artifacts/${encodedPath}?redirect=0`, { isRequestCurrent }
    );
    if (data.error) throw new ApiError(data.message || 'Failed to resolve download', 400, data);
    return data.url || null;
  },

  fetchArtifactBinary: async (taskId, artifactPath, { asProxy = false, isRequestCurrent } = {}) => {
    const encodedPath = artifactPath.split('/').map(encodeURIComponent).join('/');
    return fetchBinaryWithAuth(
      `/json/tasks/${encodeURIComponent(taskId)}/artifacts/${encodedPath}${asProxy ? '?proxy=1' : ''}`, { isRequestCurrent }
    );
  },

  getTaskEventsSseUrl: async (taskId, since = 0) => {
    await awaitAuthHydration();
    const authStore = getAuthStore().getState();
    let token = authStore.token;
    if (token && authStore.isTokenExpired()) token = await authStore.getValidToken();
    if (!token) throw new ApiError('Sign in to follow live activity.', 401);
    const params = new URLSearchParams({ token });
    const seq = Math.floor(Number(since) || 0);
    if (seq > 0) params.set('since', String(seq));
    return `${API_HOST}/json/tasks/${encodeURIComponent(taskId)}/events?${params.toString()}`;
  },

  fetchSchedules: async () => {
    const data = await fetchWithAuth('/json/task-schedules');
    if (data.error) throw new ApiError(data.message || 'Failed to fetch schedules', 400, data);
    return data.schedules;
  },

  fetchCheckpoints: async (taskId) => {
    const data = await fetchWithAuth(`/json/tasks/${encodeURIComponent(taskId)}/checkpoints`);
    if (data.error) throw new ApiError(data.message || 'Failed to load checkpoints', data.status || 400, data);
    return data;
  },

  fetchCheckpointDiff: async (taskId, { from, to, path } = {}) => {
    const params = new URLSearchParams();
    if (from != null) params.set('from', String(from));
    if (to != null) params.set('to', String(to));
    if (path) params.set('path', String(path));
    const qs = params.toString();
    const data = await fetchWithAuth(`/json/tasks/${encodeURIComponent(taskId)}/diff${qs ? `?${qs}` : ''}`);
    if (data.error) throw new ApiError(data.message || 'Failed to load diff', data.status || 400, data);
    return data;
  },

  restoreCheckpoint: async (taskId, { turn, sha } = {}) => {
    const data = await fetchTaskControl(`/json/tasks/${encodeURIComponent(taskId)}/restore`, {
      method: 'POST',
      body: sha ? { sha } : { turn },
    });
    if (data.error) throw new ApiError(data.message || 'Failed to restore', data.status || 400, data);
    return data;
  },

  answerQuestion: async (taskId, { id, answer, optionId, skipped } = {}) => {
    const data = await fetchTaskControl(`/json/tasks/${encodeURIComponent(taskId)}/ask`, {
      method: 'POST',
      body: { id, answer, optionId, skipped: skipped === true },
      // Must outlast the control-plane's 45s orchestrator wait. 15s aborted
      // while the host was still accepting the answer and put the card back.
      timeoutMs: 60000,
    });
    if (data.error) throw new ApiError(data.message || 'Failed to send answer', data.status || 400, data);
    return data;
  },

  confirmBrowserAction: async (taskId, { id, approved }) => {
    const data = await fetchTaskControl(`/json/tasks/${encodeURIComponent(taskId)}/browser/confirm`, {
      method: 'POST',
      body: { id, approved: !!approved },
      timeoutMs: 60000,
    });
    if (data.error) throw new ApiError(data.message || 'Failed to confirm action', data.status || 400, data);
    return data;
  },

  submitBrowserLogin: async (taskId, { id, values, skip = false } = {}) => {
    const data = await fetchTaskControl(`/json/tasks/${encodeURIComponent(taskId)}/browser/login`, {
      method: 'POST',
      body: skip ? { id, skip: true } : { id, values },
    });
    if (data.error) throw new ApiError(data.message || 'Could not send the sign-in', data.status || 400, data);
    return data;
  },

  getBrowserViewerUrl: async (taskId, { watch = false } = {}) => {
    await awaitAuthHydration();
    const authStore = getAuthStore().getState();
    const current = () => {
      const latest = getAuthStore().getState();
      return !!latest.token && !latest._isSigningOut && latest.user?.id === authStore.user?.id && latest._sessionGeneration === authStore._sessionGeneration;
    };
    let token = authStore.token;
    if (token && authStore.isTokenExpired()) token = await authStore.getValidToken();
    if (!token) throw new ApiError('Sign in to view the browser.', 401);
    // WebSockets cannot carry native proof headers. Authorize an ordinary
    // request first, then consume its single-use ticket at the WS upgrade.
    let ticket = '';
    if (!watch) {
      const grant = await fetchTaskControl(`/json/tasks/${encodeURIComponent(taskId)}/browser/viewer-ticket`, { method: 'POST', body: {} });
      if (grant?.error || !/^[a-f0-9]{64}$/.test(grant?.ticket || '')) throw new Error(grant?.message || 'Could not authorize browser control. Reopen the browser.');
      ticket = `&ticket=${encodeURIComponent(grant.ticket)}`;
    }
    if (!current()) throw new ApiError('Your account changed. Open the browser again.', 401);
    const wsBase = API_HOST.replace(/^http/, 'ws');
    return `${wsBase}/json/tasks/${encodeURIComponent(taskId)}/browser/ws?token=${encodeURIComponent(token)}${watch ? '&watch=1' : ticket}`;
  },

  createSchedule: async (body) => {
    const data = await fetchWithAuth('/json/task-schedules', { method: 'POST', body });
    if (data.error) throw new ApiError(data.message || 'Failed to create schedule', data.status || 400, data);
    return data;
  },

  deleteSchedule: async (scheduleId) => {
    const data = await fetchWithAuth(`/json/task-schedules/${encodeURIComponent(scheduleId)}`, { method: 'DELETE' });
    if (data.error) throw new ApiError(data.message || 'Failed to delete schedule', data.status || 400, data);
    return data;
  },

  updateSchedule: async (scheduleId, body) => {
    const data = await fetchWithAuth(`/json/task-schedules/${encodeURIComponent(scheduleId)}`, { method: 'POST', body });
    if (data.error) throw new ApiError(data.message || 'Failed to update schedule', data.status || 400, data);
    return data;
  },

  runScheduleNow: async (scheduleId) => {
    const data = await fetchWithAuth(`/json/task-schedules/${encodeURIComponent(scheduleId)}/run`, { method: 'POST' });
    if (data.error) throw new ApiError(data.message || 'Failed to run schedule', data.status || 400, data);
    return data;
  },
};
